type RecordObject = Record<string, unknown>;

function asRecord(value: unknown): RecordObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordObject)
    : null;
}

function readLogId(log: unknown): string {
  const record = asRecord(log);
  if (!record) {
    return '';
  }

  const logId = record.logId ?? record.log_id;
  return typeof logId === 'string' ? logId : '';
}

export function compactRecordObjects(values: unknown[]): unknown[] {
  return values.filter((value) => asRecord(value) !== null);
}

export function unwrapLinLogs(output: unknown): unknown[] {
  if (Array.isArray(output)) {
    return compactRecordObjects(output);
  }

  const record = asRecord(output);
  if (record && Array.isArray(record.logs)) {
    return compactRecordObjects(record.logs);
  }

  return record ? [record] : [];
}

export function readNormalizedTelemetries(log: unknown): unknown[] {
  const record = asRecord(log);
  const telemetries = record?.telemetries ?? record?.Telemetries;
  return Array.isArray(telemetries) ? compactRecordObjects(telemetries) : [];
}

function isInfoOrUnknownSeverity(severity: unknown): boolean {
  if (typeof severity === 'number') {
    return severity === 0 || severity === 1;
  }

  if (typeof severity === 'string') {
    const normalized = severity.toUpperCase();
    return (
      normalized === 'INFO' ||
      normalized === 'UNKNOWN_EVENT' ||
      normalized === 'UNKNOWN'
    );
  }

  return false;
}

function readEventSeverity(event: unknown): unknown {
  const record = asRecord(event) ?? {};
  if ('eventSeverity' in record || 'event_severity' in record) {
    return record.eventSeverity ?? record.event_severity;
  }

  const legacyType = record.eventType ?? record.event_type;
  if (typeof legacyType === 'string' || legacyType === 0) {
    return legacyType;
  }

  return undefined;
}

export function readNormalizedEvents(log: unknown): unknown[] {
  const record = asRecord(log);
  const events = record?.events ?? record?.Events;
  return Array.isArray(events) ? compactRecordObjects(events) : [];
}

/** Production parity: ADIE runs only when telemetries are present. */
export function shouldForwardToAdie(log: unknown): boolean {
  return readNormalizedTelemetries(log).length > 0;
}

/** Production parity: ICS skips logs whose events include INFO or UNKNOWN. */
export function shouldForwardToIcs(log: unknown): boolean {
  const events = readNormalizedEvents(log);

  if (events.length === 0) {
    return true;
  }

  return !events.some((event) =>
    isInfoOrUnknownSeverity(readEventSeverity(event)),
  );
}

export function filterLogsForAdie(logs: unknown[]): unknown[] {
  return sortLogsByOccurredAtAsc(
    compactRecordObjects(logs).filter(shouldForwardToAdie),
  );
}

function readOccurredAtMs(log: unknown): number {
  const record = asRecord(log);
  if (!record) {
    return Number.POSITIVE_INFINITY;
  }

  const context = asRecord(record.context);
  const raw =
    context?.occurredAt ??
    context?.occurred_at ??
    record.occurredAt ??
    record.occurred_at;

  if (raw instanceof Date) {
    const ms = raw.getTime();
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }

  if (typeof raw === 'string' && raw.trim() !== '') {
    const ms = Date.parse(raw);
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
  }

  return Number.POSITIVE_INFINITY;
}

/** Ascending by context.occurredAt — ADIE stateful tiers need oldest-first. */
export function sortLogsByOccurredAtAsc(logs: unknown[]): unknown[] {
  return [...logs].sort(
    (left, right) => readOccurredAtMs(left) - readOccurredAtMs(right),
  );
}

export function filterLogsForIcs(logs: unknown[]): unknown[] {
  return compactRecordObjects(logs).filter(shouldForwardToIcs);
}

export function extractAnomalyEventsFromAdieOutput(
  adieOutput: unknown,
): unknown[] {
  const record = asRecord(adieOutput);
  const responses = Array.isArray(record?.responses)
    ? record.responses
    : Array.isArray(adieOutput)
      ? adieOutput
      : adieOutput === null || adieOutput === undefined
        ? []
        : [adieOutput];

  return responses.flatMap((response) => {
    const responseRecord = asRecord(response) ?? {};
    const events = responseRecord.anomaly_event ?? responseRecord.anomalyEvent;
    return Array.isArray(events) ? compactRecordObjects(events) : [];
  });
}

export type AdieRoutingSummary = {
  totalLinLogs: number;
  forwardedToAdie: number;
  skippedNoTelemetries: number;
  anomalyEvents: number;
  forwardedLogIds: string[];
  skippedLogIds: string[];
};

export type IcsRoutingSummary = {
  totalLinLogs: number;
  forwardedToIcs: number;
  skippedInfoOrUnknown: number;
  anomalyEvents: number;
  forwardedLogIds: string[];
  skippedLogIds: string[];
};

export function buildAdieRoutingSummary(
  linLogs: unknown[],
  adieLogs: unknown[],
  anomalyEvents: unknown[],
): AdieRoutingSummary {
  const adieLogIds = new Set(adieLogs.map(readLogId).filter(Boolean));

  return {
    totalLinLogs: linLogs.length,
    forwardedToAdie: adieLogs.length,
    skippedNoTelemetries: linLogs.length - adieLogs.length,
    anomalyEvents: anomalyEvents.length,
    forwardedLogIds: adieLogs.map(readLogId).filter(Boolean),
    skippedLogIds: linLogs
      .map(readLogId)
      .filter((logId) => logId && !adieLogIds.has(logId)),
  };
}

export function buildIcsRoutingSummary(
  linLogs: unknown[],
  icsLogs: unknown[],
  anomalyEvents: unknown[],
): IcsRoutingSummary {
  const icsLogIds = new Set(icsLogs.map(readLogId).filter(Boolean));

  return {
    totalLinLogs: linLogs.length,
    forwardedToIcs: icsLogs.length,
    skippedInfoOrUnknown: linLogs.length - icsLogs.length,
    anomalyEvents: anomalyEvents.length,
    forwardedLogIds: icsLogs.map(readLogId).filter(Boolean),
    skippedLogIds: linLogs
      .map(readLogId)
      .filter((logId) => logId && !icsLogIds.has(logId)),
  };
}
